import type * as openapi from '@/helpers/api/types/openapiOverrides';

import { onBeforeUnmount, ref } from 'vue';
import { useDebounceFn } from '@vueuse/core';

import { fetchGetJobPriceSc } from '@/helpers/fetchers';

export type PriceFetchKind = 'quantity' | 'custom_fee';

interface UseJobEditServicePriceOptions {
  jobId: () => number | undefined;
  jobDate: () => string | undefined;
  legNumber: () => number | undefined;
  serviceCode: () => string | undefined;
  /** Debounce delay in ms for the debounced variant (default 300) */
  debounceMs?: number;
}

export function useJobEditServicePrice(opts: UseJobEditServicePriceOptions) {
  const isLoading = ref(false);
  let controller: AbortController | undefined;

  async function fetchPrice(
    kind: PriceFetchKind,
    value: number,
  ): Promise<openapi.GetJobPriceSc_200 | undefined> {
    const serviceCode = opts.serviceCode();
    const jobId = opts.jobId();
    const jobDate = opts.jobDate();
    const legNumber = opts.legNumber();
    if (!serviceCode || !jobId || !jobDate || !legNumber) return undefined;

    if (controller) controller.abort();
    controller = new AbortController();

    isLoading.value = true;
    try {
      return await fetchGetJobPriceSc(
        {
          job_id: jobId,
          job_date: jobDate,
          leg: legNumber,
          service_code: serviceCode,
          ...(kind === 'quantity'
            ? { quantity: value }
            : { custom_fee: value }),
        },
        { signal: controller.signal },
      );
    } catch {
      // fetcher handles notification + Sentry + isCancel filtering; just
      // signal "no result" upwards so the caller skips applying.
      return undefined;
    } finally {
      isLoading.value = false;
    }
  }

  const debouncedFetchPrice = useDebounceFn(
    (kind: PriceFetchKind, value: number) => fetchPrice(kind, value),
    opts.debounceMs ?? 300,
  );

  onBeforeUnmount(() => {
    if (controller) controller.abort();
  });

  return {
    isLoading,
    fetchPrice,
    debouncedFetchPrice,
  };
}
